import { ApiError } from '@eneco/dc/client/ApiError';
import { postCustomerDiscontinueConfirm } from '@eneco/dc/services';
import { MachineConfig } from '@eneco/flows/types';
import {
  DC_Customers_Client_Models_CustomerDiscontinueConfirmRequestModel,
  ResponseModels_CustomerAccountSummary_CustomerAccountSummary,
} from '@monorepo-types/dc';
import { assign, fromPromise, setup } from 'xstate';

interface InputType {
  event: SubmitEvent;
}

interface OutputType {
  data: unknown;
}

interface OverviewMachineContext {
  errorMessage?: string;
  summary?: ResponseModels_CustomerAccountSummary_CustomerAccountSummary;
}

type SummaryEvent =
  | {
      type: 'SUBMIT';
      config: MachineConfig;
      values: {
        customerId?: number | null;
        deceaesedInformation: DC_Customers_Client_Models_CustomerDiscontinueConfirmRequestModel;
      };
    }
  | { type: 'RETRY_SUBMIT' }
  | { type: 'RESET' };

type SubmitEvent = Extract<SummaryEvent, { type: 'SUBMIT' }>;

const deceaesedCancelation = (
  config: MachineConfig,
  customerId: number | undefined | null,
  deceaesedInformation: DC_Customers_Client_Models_CustomerDiscontinueConfirmRequestModel,
): Promise<OutputType> => {
  return new Promise((resolve, reject) =>
    postCustomerDiscontinueConfirm({
      businessUnit: config.businessUnit,
      label: config.label,
      customerId: Number(customerId),
      requestBody: {
        data: {
          customer: deceaesedInformation.customer,
          contactPerson: deceaesedInformation.contactPerson,
        },
      },
    })
      .then(response => {
        resolve({ data: response });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      }),
  );
};

export const summaryMachine = setup({
  types: {} as {
    context: OverviewMachineContext;
    events: SummaryEvent;
    actors: {
      submitData: {
        input: InputType;
        output: OutputType;
      };
    };
  },
  actors: {
    submitData: fromPromise<OutputType, InputType>(({ input }) => {
      const { event } = input;
      if (event.type !== 'SUBMIT') {
        throw new Error('Invalid event type');
      }
      return deceaesedCancelation(event.config, event.values.customerId, event.values.deceaesedInformation);
    }),
  },
}).createMachine({
  id: 'summary-machine',
  initial: 'IDLE',
  context: {
    summary: undefined,
    errorMessage: undefined,
  },
  states: {
    IDLE: {
      on: {
        SUBMIT: { target: 'SUBMITTING' },
      },
    },
    SUBMITTING: {
      invoke: {
        src: 'submitData',
        input: ({ event }) => {
          if (event.type !== 'SUBMIT') {
            throw new Error('Invalid event type for submitData actor');
          }
          return { event };
        },
        onDone: {
          target: 'SUCCESS',
        },
        onError: {
          target: 'ERROR',
          actions: assign({
            errorMessage: ({ event }) => (event.error as Error).message,
          }),
        },
      },
    },
    SUCCESS: {
      type: 'final',
    },
    ERROR: {
      on: {
        RETRY_SUBMIT: { target: 'SUBMITTING' },
        RESET: { target: 'IDLE' },
      },
    },
  },
});
